import * as com from "../Common.js"

const num = (value) => com.safeParseInt(value)

const text = (value) => `${value || ""}`.trim()

const personName = (person) => {
    if (!person) return ""
    const first = text(person.firstName || person.firstname)
    const last = text(person.lastName || person.lastname)
    const joined = [first, last].filter(Boolean).join(" ")
    return joined || text(person.name)
}

const participantIds = (application) => {
    const byId = application?.participantsById || {}
    const order = Array.isArray(application?.participantOrder) ? application.participantOrder : []
    const seen = new Set()
    const ids = []
    order.concat(Object.keys(byId)).forEach((id) => {
        if (!id || seen.has(id) || !byId[id]) return
        seen.add(id)
        ids.push(id)
    })
    return ids.sort((a, b) => {
        const left = byId[a] || {}
        const right = byId[b] || {}
        const leftPrimary = left.isPrimary || a === application?.primaryPartyId ? 0 : 1
        const rightPrimary = right.isPrimary || b === application?.primaryPartyId ? 0 : 1
        if (leftPrimary !== rightPrimary) return leftPrimary - rightPrimary
        return (left.position || 0) - (right.position || 0)
    })
}

export const formatBorrowerName = (application) => {
    const header = getBorrowerHeaderDisplay(application)
    return header.fullLabel
}

/** Compact header label: primary only, or "Primary + N coborrowers". */
export const getBorrowerHeaderDisplay = (application) => {
    if (!application) {
        return { primaryName: "-", coCount: 0, fullNames: [], fullLabel: "-", compactLabel: "-" }
    }
    const ids = participantIds(application)
    const participants = ids
        .map((id) => application?.participantsById?.[id])
        .filter(Boolean)
    const fullNames = participants.map((person, index) => {
        const name = personName(person)
        if (name) return name
        return index === 0 ? "Borrower (name missing)" : `Co-borrower ${index} (name missing)`
    })
    const primaryName = personName(participants[0]) || "Borrower"
    const coCount = Math.max(0, participants.length - 1)
    const fullLabel = fullNames.length ? fullNames.join(" & ") : "-"
    const compactLabel = coCount > 0
        ? `${primaryName} + ${coCount} coborrower${coCount > 1 ? "s" : ""}`
        : primaryName
    return { primaryName, coCount, fullNames, fullLabel, compactLabel }
}

export const formatPurpose = (purpose) => {
    if (purpose === "purchase") return "Purchase"
    if (purpose === "cashoutrefinance") return "Cash-out refinance"
    if (purpose === "refinance") return "Refinance"
    return "TBD"
}

export const formatAddress = (property) => {
    if (!property) return "-"
    const street = text(property.address)
    const city = text(property.city)
    const state = text(property.state)
    const zip = text(property.zipcode || property.zip)
    const cityStateZip = [city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(" ")
    if (!cityStateZip) return street || "-"
    const streetLower = street.toLowerCase()
    if (
        street
        && city
        && streetLower.includes(city.toLowerCase())
        && (!zip || streetLower.includes(zip.toLowerCase()))
    ) {
        return street
    }
    return [street, cityStateZip].filter(Boolean).join(" ") || "-"
}

export const formatLtv = (application) => {
    // Match the header Loan Amount: LTV = loansize / property value.
    // Do not derive from balance ± paydown/cashout — stale paydown after clearing
    // current balance produced negative LTVs (e.g. -12%).
    const purpose = application?.property?.purpose
    const rawLoansize = application?.selection?.loansize
    if (rawLoansize === "" || rawLoansize === undefined || rawLoansize === null) return "-"
    const loansize = num(rawLoansize)
    const price = purpose === "purchase"
        ? parseFloat(application?.property?.salesprice)
        : parseFloat(application?.property?.appraisal)
    if (!(price > 0) || !(loansize >= 0) || !Number.isFinite(loansize)) return "-"
    const ltv = Math.round((100 * loansize) / price)
    if (!Number.isFinite(ltv) || ltv < 0) return "-"
    return `${ltv}%`
}

export const formatLoanAmount = (loansize) => {
    if (loansize === "" || loansize === undefined || loansize === null) return "-"
    const amount = com.commaize(loansize)
    return amount ? `$${amount}` : "-"
}

export const loanBaseRate = (loan) => {
    if (!loan || typeof loan !== "object") return 0
    const raw = loan.base_rate ?? loan.Base_rate ?? loan._base_rate
    const rate = Number(raw)
    return Number.isFinite(rate) ? rate : 0
}

export const getDisplayRateNumber = (application) => {
    const selection = application?.selection || {}
    const selected = selection.selectedloan
    const recommended = Array.isArray(selection.loSelectedLoans) ? selection.loSelectedLoans : []
    let rate = loanBaseRate(selected)
    if (!rate && selected?.hash) {
        const match = recommended.find((loan) => (loan?.hash || loan?._hash) === selected.hash)
        rate = loanBaseRate(match)
    }
    if (!rate) {
        for (const loan of recommended) {
            rate = loanBaseRate(loan)
            if (rate) break
        }
    }
    return rate || 0
}

export const formatRate = (application) => {
    const rate = getDisplayRateNumber(application)
    if (!rate) return "Select"
    return `${rate}%`
}
